import React, {Fragment, useRef, useState, useEffect, useCallback, type RefObject} from "react";
import styled from "@emotion/styled";
import {defaultTo, partial, map} from "lodash-es";
import { getFetchedLeaderboardPages, type getLeaderboard} from "modules/selectors";
import {Body} from "components/Leaderboard/leaderboardContent/leaderboards/leaderboardTable/styles";
import LeaderboardRow from "components/Leaderboard/leaderboardContent/leaderboards/leaderboardTable/LeaderboardRow";
import {useSelector} from "react-redux";
import {Exist} from "components/Exist";
import {useIsMobile} from "modules/utils";
import {IconButton} from "@mui/material";
import {useTranslation} from "react-i18next";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import type {LeaderboardsTabsType} from "modules";
import {Preloader} from "components/Preloader";

const Wrapper = styled.div`
	display: flex;
	flex-flow: column;
	height: 100%;
	max-height: 100%;
	overflow: hidden;
	position: relative;
	@media (max-width: 880px) {
		padding: 0;
		position: static;
	}
`;

const CloseArea = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 11;
	background: #000;
	width: 100%;
	padding-right: 20px;
	&.hidden {
		transform: translateY(-100%);
		visibility: hidden;
	}
	@keyframes toBottom {
		from {
			transform: translateY(-100%);
		}
		to {
			transform: translateY(0);
		}
	}

	animation: toBottom 0.3s ease forwards;
	svg {
		color: #fff;
		height: 34px;
		width: 34px;
	}

	span {
		color: #ffffff;
		font-weight: 700;
		font-size: 16px;
	}
`;

const DesktopToTop = styled(CloseArea)`
	position: absolute;
`;

export enum OutUserType {
	TOP = "top",
	DOWN = "down",
}

interface IScrollProps {
	ownUserRowRef: RefObject<HTMLLIElement>;
	tableRef: RefObject<HTMLOListElement>;
	isLoading: boolean;
	isScrolledState: RefObject<boolean>;
	firstPage: number;
	lastPage: number;
	setPage: (page: number) => void;
	setOwnUserOut: (value: OutUserType | null) => void;
	leaderboards: ReturnType<typeof getLeaderboard>;
}

const useMobileScrollEvents = ({
	leaderboards,
	isLoading,
	tableRef,
	isScrolledState,
	firstPage,
	setPage,
	lastPage,
	ownUserRowRef,
	setOwnUserOut,
}: IScrollProps) => {
	const restoreScrollMobile = useRef(0);
	const fetchedPages = useSelector(getFetchedLeaderboardPages);
	const isMobile = useIsMobile();
	const [showBackButton, setShowBackButton] = useState(false);
	useEffect(() => {
		if (restoreScrollMobile.current && isMobile) {
			const newScroll = defaultTo(document.scrollingElement?.scrollHeight, 0);
			const currentScroll = newScroll - restoreScrollMobile.current;
			document.scrollingElement?.scrollTo({
				left: 0,
				top: currentScroll,
				behavior: "auto",
			});
			restoreScrollMobile.current = 0;
		}
	}, [leaderboards, isMobile]);

	const onScroll = useCallback(() => {
		if (document.scrollingElement && tableRef.current) {
			const {scrollTop, scrollHeight, clientHeight} = document.scrollingElement;

			const deltaScroll = 50;
			const deltaBottom = scrollHeight - scrollTop - clientHeight;

			const isLoadMoreDown = [
				deltaBottom < deltaScroll,
				deltaBottom > 0,
				leaderboards.nextPage,
				!isLoading,
			].every(Boolean);

			if (isLoadMoreDown) {
				setPage(lastPage + 1);
				return;
			}

			const offsetTop = scrollHeight - tableRef.current.scrollHeight;
			const userRowOffset = defaultTo(ownUserRowRef.current?.offsetTop, 0);
			const height = defaultTo(ownUserRowRef.current?.clientHeight, 0);
			const parentHeight = defaultTo(clientHeight, 0);
			const isNotVisibleTop = scrollTop >= userRowOffset + offsetTop - height;
			const isNotVisibleBottom =
				scrollTop + parentHeight <= userRowOffset + offsetTop - height;

			if (height) {
				setOwnUserOut(getUserHideType(isNotVisibleTop, isNotVisibleBottom));
			} else {
				setOwnUserOut(OutUserType.DOWN);
			}

			const isAboutToTopTable = scrollTop < offsetTop;

			if (isAboutToTopTable) {
				const isLoadMoreUp = [
					!isLoading,
					isScrolledState.current,
					Boolean(firstPage - 1),
					!fetchedPages.includes(firstPage - 1),
				].every(Boolean);

				if (isLoadMoreUp) {
					setPage(firstPage - 1);
					restoreScrollMobile.current = scrollHeight - offsetTop;
				}
			}

			setShowBackButton(!isAboutToTopTable);
		}
	}, [tableRef, leaderboards.nextPage, isLoading, ownUserRowRef, setPage, lastPage, setOwnUserOut, isScrolledState, firstPage, fetchedPages]);

	useEffect(() => {
		if (isMobile) {
			document.addEventListener("scroll", onScroll);
		}
		return () => {
			if (isMobile) {
				document.removeEventListener("scroll", onScroll);
			}
		};
	}, [isMobile, onScroll]);

	const toTopMobile = () => {
		setPage(1);
		document.scrollingElement?.scrollTo({
			left: 0,
			top: 0,
			behavior: "smooth",
		});
	};

	return {
		showBackButtonMobile: showBackButton,
		toTopMobile,
	};
};

const getUserHideType = (top: boolean, bottom: boolean) => {
	if (top) {
		return OutUserType.TOP;
	}
	if (bottom) {
		return OutUserType.DOWN;
	}

	return null;
};

const useDesktopScrollEvents = ({
	ownUserRowRef,
	isLoading,
	leaderboards,
	setPage,
	firstPage,
	lastPage,
	setOwnUserOut,
	tableRef,
}: IScrollProps) => {
	const restoreScroll = useRef(0);
	const fetchedPages = useSelector(getFetchedLeaderboardPages);
	const isDesktop = !useIsMobile();
	const [showBackButton, setShowBackButton] = useState(false);

	useEffect(() => {
		if (restoreScroll.current) {
			const parentElement = tableRef.current;
			const newScroll = defaultTo(parentElement?.scrollHeight, 0);

			const currentScroll = newScroll - restoreScroll.current;

			parentElement?.scrollTo(0, currentScroll);
			restoreScroll.current = 0;
		}
	}, [leaderboards, tableRef]);

	const onScroll = useCallback(
		(e: Event) => {
			const currentTarget = e.currentTarget as HTMLDivElement;

			if (!isLoading && currentTarget) {
				const scrollTop = currentTarget.scrollTop;
				const isLoadMoreUp = [
					scrollTop === 0,
					Boolean(firstPage - 1),
					!fetchedPages.includes(firstPage - 1),
				].every(Boolean);

				if (isLoadMoreUp) {
					setPage(firstPage - 1);
					restoreScroll.current = defaultTo(tableRef.current?.scrollHeight, 0);
					return;
				}

				const userRowOffset = defaultTo(ownUserRowRef.current?.offsetTop, 0);
				const height = defaultTo(ownUserRowRef.current?.clientHeight, 0);
				const parentHeight = defaultTo(
					ownUserRowRef.current?.parentElement?.clientHeight,
					0
				);

				const deltaScroll = 50;

				const isNotVisibleTop = scrollTop >= userRowOffset + height;
				const isNotVisibleBottom = scrollTop + parentHeight <= userRowOffset;

				if (height) {
					setOwnUserOut(getUserHideType(isNotVisibleTop, isNotVisibleBottom));
				} else {
					setOwnUserOut(OutUserType.DOWN);
				}

				const scrollValue =
					currentTarget.scrollHeight - currentTarget.clientHeight - deltaScroll;
				const isRequestMore = scrollValue < scrollTop && scrollTop - scrollValue > 0;

				if ([isRequestMore, leaderboards.nextPage].every(Boolean)) {
					setPage(lastPage + 1);
				}

				setShowBackButton(scrollTop > deltaScroll);
			}
		},
		[isLoading, firstPage, fetchedPages, ownUserRowRef, leaderboards.nextPage, setPage, tableRef, setOwnUserOut, lastPage]
	);

	useEffect(() => {
		const elem = tableRef.current;

		if (!elem) {
			return ;
		}

		if (isDesktop) {
			elem.addEventListener("scroll", onScroll);
		}

		return () => {
			if (isDesktop) {
				elem.removeEventListener("scroll", onScroll);
			}
		};
	}, [isDesktop, onScroll, tableRef]);

	const toTopDesktop = () => {
		setPage(1);
		tableRef.current?.scrollTo({
			left: 0,
			top: 0,
			behavior: "smooth",
		});
	};

	return {
		showBackButtonDesktop: showBackButton,
		toTopDesktop,
	};
};

interface IProps {
	leaderboards: ReturnType<typeof getLeaderboard>;
	userId: number;
	isLoading: boolean;
	setPage: (page: number) => void;
	lastPage: number;
	firstPage: number;
	currentPage: number;
	isNeedToScroll: boolean;
	setIsNeedToScroll: (isNeedToScroll: boolean) => void;
	selectedTab: LeaderboardsTabsType;
}

const LeaderboardTable: React.FC<IProps> = ({
	leaderboards,
	userId,
	isLoading,
	lastPage,
	firstPage,
	currentPage,
	setPage,
	isNeedToScroll,
	setIsNeedToScroll,
	selectedTab,
}) => {
	const i18n = useTranslation();
	const isMobile = useIsMobile();
	const ownUserRowRef = useRef<HTMLLIElement>(null);
	const tableRef = useRef<HTMLOListElement>(null);

	const isUserInCurrentList = leaderboards.ranks.some(
		(it) => it.userId === leaderboards.user?.userId
	);

	const [visibilityUserState, setOwnUserOut] = useState<OutUserType | null>(null);

	const isScrolledState = useRef(true);

	const scrollProps: IScrollProps = {
		leaderboards,
		lastPage,
		firstPage,
		tableRef,
		setPage,
		setOwnUserOut,
		ownUserRowRef,
		isLoading,
		isScrolledState,
	};

	const {showBackButtonMobile, toTopMobile} = useMobileScrollEvents(scrollProps);

	const {showBackButtonDesktop, toTopDesktop} = useDesktopScrollEvents(scrollProps);

	const onScrollToUsersRow = () => {
		isScrolledState.current = false;
		ownUserRowRef.current?.scrollIntoView({behavior: "smooth", block: "center"});
		const SCROLL_START_DELAY = 500;
		setTimeout(() => {
			isScrolledState.current = true;
		}, SCROLL_START_DELAY);
	};

	useEffect(() => {
		if (isNeedToScroll && isUserInCurrentList) {
			onScrollToUsersRow();
			setIsNeedToScroll(false);
		}
	}, [isNeedToScroll, isUserInCurrentList, leaderboards, setIsNeedToScroll]);

	useEffect(() => {
		if (!isUserInCurrentList) {
			if (leaderboards.user?.page) {
				setOwnUserOut(
					currentPage > leaderboards.user?.page ? OutUserType.TOP : OutUserType.DOWN
				);
			} else {
				setOwnUserOut(OutUserType.DOWN);
			}
		} else {
			setOwnUserOut(null);
		}
	}, [isUserInCurrentList, currentPage, leaderboards.user?.page]);

	const onClickOwnUser = (page: number | null) => {
		if (isUserInCurrentList) {
			onScrollToUsersRow();
			return;
		}

		if (!page) {
			return;
		}

		setIsNeedToScroll(true);
		setPage(page);
	};

	return (
		<Wrapper>
			<Body
				ref={tableRef}
				role={"group"}
				aria-label={`${leaderboards.ranks.length} users, total ${leaderboards.allParticipantsCount} users,`}
			>
				<Exist when={isMobile}>
					<CloseArea
						role={"banner"}
						aria-hidden={!showBackButtonMobile}
						className={!showBackButtonMobile ? "hidden" : ""}
					>
						<IconButton onClick={toTopMobile} aria-labelledby={"to-top-button"}>
							<ExpandLessRoundedIcon role={"presentation"} />{" "}
							<span id={"to-top-button"} role={"presentation"}>
								{i18n.t("leaderboards.table.to_top", "To Top")}
							</span>
						</IconButton>
					</CloseArea>
				</Exist>

				<Exist when={!isMobile}>
					<DesktopToTop
						role={"banner"}
						aria-live={"polite"}
						aria-hidden={!showBackButtonMobile}
						className={!showBackButtonDesktop ? "hidden" : ""}
					>
						<IconButton
							onClick={toTopDesktop}
							aria-labelledby={"to-top-button-desktop"}
						>
							<ExpandLessRoundedIcon role={"presentation"} />{" "}
							<span id={"to-top-button-desktop"} role={"presentation"}>
								{i18n.t("leaderboards.table.to_top", "To Top")}
							</span>
						</IconButton>
					</DesktopToTop>
				</Exist>

				{map(leaderboards.ranks, (userRank, i) => {
					const indexRank = i + 1;
					return (
						<Fragment key={indexRank}>
							<LeaderboardRow
								userId={userId}
								userRank={userRank}
								ownUserRowRef={ownUserRowRef}
								selectedTab={selectedTab}
								isLoading={isLoading}
							/>
						</Fragment>
					);
				})}

				{leaderboards.user && visibilityUserState && (
					<LeaderboardRow
						userRank={leaderboards.user}
						userId={userId}
						onClickOwnUser={partial(onClickOwnUser, leaderboards.user.page)}
						selectedTab={selectedTab}
						stickyType={visibilityUserState}
						isLoading={isLoading}
					/>
				)}
			</Body>

			<Exist when={isLoading}>
				<Preloader noBackground={true} />
			</Exist>
		</Wrapper>
	);
};

export default React.memo(LeaderboardTable);
